// Imports for external libraries go here.
import React, { FC, useContext, useEffect, useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { Heading, Messages, RichText, Types } from '@marriott/mi-ui-library';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';
import { generateApolloClientHeaders } from '../../modules/utils';
import { PageContext } from '../../modules/context/PageContext';
import { phoenixAccountGetTierBenefitsContent } from '../../modules/graph';
import { AccountTable } from '../AccountTable/AccountTable';
import { MembershipTiersView, MembershipTiersViewSkeleton, AccountTableSkeleton } from './MemberShipTiersBenefitsView';
import type {
  MembershipTierContent,
  MemberShipTiersBenefitsProps,
  TierBenefitsData,
} from './MemberShipTiersBenefits.types';
import { StyledMemberShipTiersBenefits } from './MemberShipTiersBenefits.styles';
import { membershipTiersBenefitsMockData } from './__mock__/MemberShipTiersBenefits.mock';
import {
  buildHeaderTitleItems,
  buildAccountTableRowItems,
  getBenefitDisclaimerFromTiers,
  getBenefits,
} from './__utils__/benefitsChart.utils';

export const MemberShipTiersBenefits: FC<MemberShipTiersBenefitsProps> = ({ isAuthorMode, model }) => {
  const { IS_LOCAL_DEV } = process.env;
  const isApiMocking = IS_LOCAL_DEV === 'true';
  const pageContext = useContext(PageContext);
  const [tierBenefitsData, setTierBenefitsData] = useState<TierBenefitsData[]>([]);
  const [tierContentByCode, setTierContentByCode] = useState<Record<string, MembershipTierContent>>({});

  const skipQuery = useMemo(() => {
    if (isApiMocking) {
      return false;
    }
    return !pageContext?.sessionData || isAuthorMode;
  }, [pageContext, isAuthorMode, isApiMocking]);

  const {
    data: aemContentData,
    loading: queryLoading,
    error: queryError,
  } = useQuery(phoenixAccountGetTierBenefitsContent, {
    variables: {
      input: {
        queryName: 'memberbenefitlist',
        locale: pageContext?.currentLocale ?? 'en-US',
      },
    },
    context: generateApolloClientHeaders(IS_LOCAL_DEV === 'true', pageContext),
    skip: skipQuery,
  });

  useEffect(() => {
    if (isAuthorMode && !isApiMocking) {
      setTierBenefitsData(
        (membershipTiersBenefitsMockData?.data?.content?.aemContentByQueryName?.content?.tiersList?.items ??
          []) as TierBenefitsData[]
      );
      return;
    }
    const content = aemContentData?.content?.aemContentByQueryName?.content?.tiersList?.items;
    if (content) {
      setTierBenefitsData(content);
    }
  }, [aemContentData, isAuthorMode, isApiMocking]);

  useEffect(() => {
    if (tierBenefitsData.length > 0) {
      const keyToDetail: Record<string, (typeof model.memberLevelDetails)[number]> = {};
      for (const detail of model?.memberLevelDetails ?? []) {
        keyToDetail[detail?.tierName] = detail;
      }

      const result: Record<string, MembershipTierContent> = {};
      for (const tierData of tierBenefitsData) {
        const tierKey = tierData?.tier;
        const detail = keyToDetail[tierKey];
        if (!detail) continue;
        result[tierKey] = {
          requirementEyebrow: detail?.nightsPerYear ?? '',
          tagline: detail?.subHeader ?? '',
          benefits: getBenefits(tierData?.benefits) ?? [],
        };
      }
      setTierContentByCode(result);
    }
  }, [tierBenefitsData]);

  const accountTableModel = useMemo(() => {
    return {
      id: 'elitebenefitsbytier',
      variation: 'DEFAULT',
      disclaimerText: model?.disclaimerText ?? '',
      headerTitleItems: buildHeaderTitleItems(model?.memberLevelDetails, model?.eliteBenefitsByTierHeader),
      rowItems: buildAccountTableRowItems(
        tierBenefitsData,
        model?.memberLevelDetails,
        model?.tracking,
        model?.displayBenefitsType,
        pageContext?.featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_BENEFITS_CHART_ORDER]
      ),
      lateCheckoutDisclaimer: getBenefitDisclaimerFromTiers(tierBenefitsData),
      displayBenefitsType: model?.displayBenefitsType,
    };
  }, [
    tierBenefitsData,
    model?.disclaimerText,
    model?.memberLevelDetails,
    model?.eliteBenefitsByTierHeader,
    model?.tracking,
    model?.displayBenefitsType,
    pageContext?.featureFlags,
  ]);

  const chartSectionHeading = model?.showBenefitsChart ? model?.sectionHeading?.trim() : '';

  if (queryError) {
    return (
      <StyledMemberShipTiersBenefits
        data-testid="account-MemberShipTiersBenefits"
        data-component-name="o-account-MemberShipTiersBenefits"
        className="container px-0"
      >
        <Messages messageType="warning" className="my-4">
          <RichText text={pageContext?.uxlErrorMessage || model?.uxlFailureErrorMessage} componentId="uxl-error-msg" />
        </Messages>
      </StyledMemberShipTiersBenefits>
    );
  }

  return (
    <StyledMemberShipTiersBenefits
      data-testid="account-MemberShipTiersBenefits"
      data-component-name="o-account-MemberShipTiersBenefits"
      className="container px-0"
    >
      {!model?.showBenefitsChart &&
        (queryLoading ? (
          <MembershipTiersViewSkeleton />
        ) : (
          <MembershipTiersView
            sectionTitle={model?.memberTiersHeader}
            tierContentByCode={tierContentByCode}
            memberLevelDetails={model?.memberLevelDetails}
            desktopColumns={model?.desktopColumns}
            tabletColumns={model?.tabletColumns}
            mobileColumns={model?.mobileColumns}
            tabLabel={model?.tabLabel}
          />
        ))}
      {model?.showBenefitsChart && (
        <>
          {chartSectionHeading ? (
            <div className="px-0 mt-md-3">
              <Heading
                variation={Types.headingType.title}
                fontSize={Types.size.small}
                element={Types.tags.div}
                titleText={chartSectionHeading}
              />
            </div>
          ) : null}
          {queryLoading ? <AccountTableSkeleton /> : <AccountTable model={accountTableModel} />}
        </>
      )}
    </StyledMemberShipTiersBenefits>
  );
};
